/**
 * mobileSessionStorage.ts
 *
 * Shared utility for persisting mobile view state (session, station, employee, boxCount)
 * to sessionStorage so that browser refreshes don't lose context on the Munbyn handheld.
 *
 * Uses sessionStorage (not localStorage) because:
 * - Scoped to the browser tab (each Munbyn device has one tab open)
 * - Auto-cleared when the tab is closed (clean slate on next shift)
 * - No stale data persists across device reboots
 */

const SESSION_STATE_KEY = 'mobile_session_state';
const WASTE_STATE_KEY = 'mobile_waste_state';
const CLOCKIN_PROGRESS_KEY = 'mobile_clockin_progress';

// --- Session State (shared across all mobile views) ---

export interface MobileSessionState {
  session: Record<string, any> | null;
  station: Record<string, any> | null;
  employee: Record<string, any> | null;
  boxCount: number;
  role: string;
  subUnitsPacked?: number;  // Track sub-unit packing progress
  subUnitIds?: number[];    // DB IDs of orphaned sub-units for current box
}

export function saveMobileState(state: MobileSessionState): void {
  try {
    sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage might be full or unavailable; silently fail
  }
}

export function loadMobileState(): MobileSessionState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearMobileState(): void {
  try {
    sessionStorage.removeItem(SESSION_STATE_KEY);
    sessionStorage.removeItem(WASTE_STATE_KEY);
    sessionStorage.removeItem(CLOCKIN_PROGRESS_KEY);
  } catch {
    // ignore
  }
}

// --- Waste Wizard State (persists wizard progress during refresh) ---

/** A single by-product entry with its selected item and weight */
export interface ByproductEntry {
  byproductItemNumber: string;  // from mapping dropdown
  weight: string;               // user-entered weight
}

export interface WasteWizardState {
  currentStep: string;
  disposableWeight: string;
  humanErrorWeight: string;
  // Dynamic by-product entry arrays (multiple items per type)
  byproductMarketableEntries: ByproductEntry[];
  byproductProductionEntries: ByproductEntry[];
  // Legacy single-value fields for backward compat with existing saved state
  byProductMarketableWeight?: string;
  byProductProductionWeight?: string;
  byProductWeight?: string;
  selectedByproductMarketable?: string;
  selectedByproductProduction?: string;
  remainderNotIntactWeight: string;
  finishProductNotIntactWeight: string;
  manualIntactWeight?: string; // Deprecated — per-case entries now stored in scannedBoxes
  scannedBoxes: Array<{ id: string; barcode: string; weight: number; lot: string; isManual?: boolean }>;
  // Material selections for by-product types
  selectedMaterialByProductMarketable: string;
  selectedMaterialByProductProduction: string;
  // Legacy field for backwards compatibility
  selectedMaterialByProduct?: string;
  selectedMaterialNotIntact: string;
  selectedMaterialIntact: string;
}

export function saveWasteWizardState(state: WasteWizardState): void {
  try {
    sessionStorage.setItem(WASTE_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function loadWasteWizardState(): WasteWizardState | null {
  try {
    const raw = sessionStorage.getItem(WASTE_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearWasteWizardState(): void {
  try {
    sessionStorage.removeItem(WASTE_STATE_KEY);
  } catch {
    // ignore
  }
}

// --- Clock-In Progress State (persists station/session between station scan and badge scan) ---

export interface ClockInProgressState {
  station: Record<string, any>;
  session: Record<string, any>;
  assignments: { operator: Record<string, any> | null; packer: Record<string, any> | null };
}

export function saveClockInProgress(state: ClockInProgressState): void {
  try {
    sessionStorage.setItem(CLOCKIN_PROGRESS_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function loadClockInProgress(): ClockInProgressState | null {
  try {
    const raw = sessionStorage.getItem(CLOCKIN_PROGRESS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearClockInProgress(): void {
  try {
    sessionStorage.removeItem(CLOCKIN_PROGRESS_KEY);
  } catch {
    // ignore
  }
}

// --- Material Issue Modal State (persists modal open + form progress during refresh) ---

const MATERIAL_ISSUE_STATE_KEY = 'mobile_material_issue_state';

export interface MaterialIssueState {
  isOpen: boolean;
  entryMode: 'scan' | 'manual';
  damagedWeight: string;
  reason: string;
  barcodeRaw: string | null;
  weightSource: 'barcode' | 'manual';
  barcodeInfo: { gtin?: string; weight?: number; lot?: string } | null;
}

export function saveMaterialIssueState(state: MaterialIssueState): void {
  try {
    sessionStorage.setItem(MATERIAL_ISSUE_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function loadMaterialIssueState(): MaterialIssueState | null {
  try {
    const raw = sessionStorage.getItem(MATERIAL_ISSUE_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearMaterialIssueState(): void {
  try {
    sessionStorage.removeItem(MATERIAL_ISSUE_STATE_KEY);
  } catch {
    // ignore
  }
}
